import { Payment } from "@b2bportal/purchase-api";
import {
  ParentState,
  cartQuoteSelectors,
  getObjectKeysAsObject,
} from "@hopper-b2b/checkout";
import { type DoneInvokeEvent, assign } from "xstate";
import type { IPaymentEventResponse } from "../../types/paymentEvents";
import { consumePaymentTypeFromStorage } from "./api/checkAuthStatus";
import {
  NubankPaymentMethod,
  type PaymentMethodsResponse,
} from "./api/getPaymentMethods";
import { PaymentState, type PaymentContext } from "./context";
import type {
  GenericPaymentError,
  SelectCreditEvent,
  SelectDebitEvent,
  SetCreditInstallment,
  SetPreAuthCheck,
} from "./events";
import {
  getSelectedCreditInstallment,
  getSelectedPaymentMethod,
} from "./selectors";

export const NubankPaymentActions = {
  setPreAuthCheck: assign((ctx: PaymentContext, event: SetPreAuthCheck) => {
    ctx[PaymentState.payment].hasAuth = event.data.hasAuth;
    ctx[PaymentState.payment].redirectUrl = event.data.redirectUrl;
    if (event.data.nupayOAuthState) {
      ctx[PaymentState.payment].nupayOAuthState = event.data.nupayOAuthState;
    }
    if (event.data.paymentType) {
      ctx[PaymentState.payment].paymentType = event.data.paymentType;
    }
    return ctx;
  }),
  setHasAuth: assign((ctx: PaymentContext) => {
    ctx[PaymentState.payment].hasAuth = true;
    return ctx;
  }),
  setAuthorizationCode: assign(
    (ctx: PaymentContext, event: IPaymentEventResponse) => {
      if (event.authorizationCode) {
        ctx[PaymentState.payment].authorizationCode = event.authorizationCode;
      }
      if (event.nupayOAuthState) {
        ctx[PaymentState.payment].nupayOAuthState = event.nupayOAuthState;
      }
      const storedPaymentType = consumePaymentTypeFromStorage();
      if (storedPaymentType) {
        ctx[PaymentState.payment].paymentType = storedPaymentType;
      }
      return ctx;
    }
  ),
  setPreAuthError: assign(
    (ctx: PaymentContext, event: DoneInvokeEvent<GenericPaymentError>) => {
      ctx[PaymentState.payment].error = {
        step: "preAuthCheck.fetch",
        details: event.data.error,
      };
      return ctx;
    }
  ),
  setRequestAuthError: assign(
    (ctx: PaymentContext, event: GenericPaymentError) => {
      ctx[PaymentState.payment].error = {
        step: "requestAuth",
        details: event.error,
      };
      return ctx;
    }
  ),
  setPaymentMethods: assign(
    (
      ctx: PaymentContext,
      event: DoneInvokeEvent<Array<PaymentMethodsResponse>>
    ) => {
      ctx[PaymentState.payment].paymentMethods = event.data;
      return ctx;
    }
  ),
  setFetchPaymentMethodsError: assign(
    (ctx: PaymentContext, event: DoneInvokeEvent<{ error?: unknown }>) => {
      ctx[PaymentState.payment].error = {
        step: "selectCreditOrDebit.fetch",
        details: event.data.error,
      };
      return ctx;
    }
  ),
  setCreditInstallment: assign(
    (ctx: PaymentContext, event: SetCreditInstallment) => {
      ctx[PaymentState.payment].selectedCreditInstallment =
        event.installmentPlan;
      return ctx;
    }
  ),
  setDebitPaymentMethod: assign(
    (ctx: PaymentContext, event: SelectDebitEvent) => {
      ctx[PaymentState.payment].selectedPaymentMethod = event.debitPayment;
      return ctx;
    }
  ),
  setCreditPaymentMethod: assign(
    (ctx: PaymentContext, event: SelectCreditEvent) => {
      ctx[PaymentState.payment].selectedPaymentMethod = event.creditPayment;
      return ctx;
    }
  ),
  // Clear payment methods when balance is zero — no payment required
  setEmptyFulfillRequestPayments: assign((ctx: PaymentContext, _event) => {
    ctx[ParentState.cartFulfill].fulfillRequestPayments = [];
    return ctx;
  }),
  setFulfillRequestPayments: assign((ctx: PaymentContext, _event) => {
    const quoteCipherToken = cartQuoteSelectors.getPriceQuoteCipherText({
      context: ctx,
    });

    const selectedPaymentMethod = getSelectedPaymentMethod({ context: ctx });

    const getIdentifier = () => {
      if (
        selectedPaymentMethod?.paymentMethodType ===
        NubankPaymentMethod.NuCredit
      ) {
        // Credit payment method
        const selectedCreditInstallment = getSelectedCreditInstallment({
          context: ctx,
        });
        return selectedCreditInstallment?.identifier;
      } else {
        // Debit payment method - ensure installmentPlans exists and has items
        const installmentPlans = selectedPaymentMethod?.installmentPlans;
        if (!installmentPlans || installmentPlans.length === 0) {
          console.error(
            "setFulfillRequestPayments: No installment plans available for debit payment method"
          );
          return undefined;
        }
        return installmentPlans[0].identifier;
      }
    };

    const isNupayDirectEnabled =
      ctx[ParentState.featureFlags]?.enableNupayDirect ?? false;

    const paymentType = (() => {
      if (!isNupayDirectEnabled) {
        return Payment.EbanxNupayPayment;
      }
      const type = ctx[PaymentState.payment].paymentType;
      if (type === "EbanxNupayPayment") {
        return Payment.EbanxNupayPayment;
      }
      return Payment.NupayPayment;
    })();

    ctx[ParentState.cartFulfill].fulfillRequestPayments = [
      {
        type: paymentType,
        value: {
          encryptedQuoteToken: quoteCipherToken?.value,
          // The identifier for the selected payment method.
          paymentMethodIdentifier: getIdentifier(),
        },
      },
    ];
    return ctx;
  }),
};

export const NubankPaymentActionTypes =
  getObjectKeysAsObject(NubankPaymentActions);
